// src/features/portal/globalAdminSlice.js
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { getFunctions, httpsCallable } from "firebase/functions";
import {
  collection,
  getDocs,
  query,
  orderBy,
  where,
  doc,
  updateDoc,
  addDoc,
  deleteDoc,
} from "firebase/firestore";
import { db } from "../../api/firebase";
import { addNotification } from "../../store/notificationSlice";

const functions = getFunctions();

export const fetchAllTenants = createAsyncThunk(
  "globalAdmin/fetchAllTenants",
  async (_, { rejectWithValue }) => {
    try {
      const tenantsRef = collection(db, "tenants");
      const q = query(tenantsRef, orderBy("name", "asc"));
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
    } catch (error) {
      return rejectWithValue(`Error al obtener los tenants: ${error.message}`);
    }
  }
);

export const fetchTenantDetails = createAsyncThunk(
  "globalAdmin/fetchTenantDetails",
  async (tenantId, { rejectWithValue }) => {
    try {
      const usersQuery = query(
        collection(db, "users"),
        where("tenantId", "==", tenantId)
      );
      const usersSnapshot = await getDocs(usersQuery);
      const users = usersSnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      const rolesRef = collection(db, `tenants/${tenantId}/roles`);
      const rolesSnapshot = await getDocs(rolesRef);
      const roles = rolesSnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      return { tenantId, users, roles };
    } catch (error) {
      return rejectWithValue(
        `Error al obtener detalles del tenant: ${error.message}`
      );
    }
  }
);

export const createNewUser = createAsyncThunk(
  "globalAdmin/createNewUser",
  async ({ targetTenantId, userData }, { dispatch, rejectWithValue }) => {
    try {
      const addUserFunction = httpsCallable(functions, "createNewUser");
      const response = await addUserFunction({
        tenantId: targetTenantId,
        userData,
      });

      dispatch(
        addNotification({
          type: "success",
          message: response.data.message,
        })
      );
      return response.data.newUser;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const updateUser = createAsyncThunk(
  "globalAdmin/updateUser",
  async ({ userId, userData }, { dispatch, rejectWithValue }) => {
    try {
      const userDocRef = doc(db, "users", userId);
      await updateDoc(userDocRef, { roleId: userData.roleId });
      dispatch(
        addNotification({
          type: "success",
          message: "Rol del usuario actualizado con éxito.",
        })
      );
      return { userId, newRoleId: userData.roleId };
    } catch (error) {
      return rejectWithValue(
        `Error al actualizar el usuario: ${error.message}`
      );
    }
  }
);

// --- NUEVO THUNK PARA ACTIVAR/DESACTIVAR USUARIOS ---
export const toggleUserActiveStatus = createAsyncThunk(
  "globalAdmin/toggleUserActiveStatus",
  async ({ userId, isActive }, { dispatch, rejectWithValue }) => {
    try {
      const toggleUserFunction = httpsCallable(
        functions,
        "toggleUserActiveStatus"
      );
      const response = await toggleUserFunction({ userId, isActive });

      dispatch(
        addNotification({
          type: "success",
          message: response.data.message,
        })
      );
      return { userId, isActive };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const addRole = createAsyncThunk(
  "globalAdmin/addRole",
  async ({ targetTenantId, roleData }, { dispatch, rejectWithValue }) => {
    try {
      const rolesRef = collection(db, `tenants/${targetTenantId}/roles`);
      const docRef = await addDoc(rolesRef, roleData);
      dispatch(
        addNotification({ type: "success", message: "Rol creado con éxito." })
      );
      return { id: docRef.id, ...roleData };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const updateRole = createAsyncThunk(
  "globalAdmin/updateRole",
  async (
    { targetTenantId, roleId, roleData },
    { dispatch, rejectWithValue }
  ) => {
    try {
      const roleDocRef = doc(db, `tenants/${targetTenantId}/roles`, roleId);
      await updateDoc(roleDocRef, roleData);
      dispatch(
        addNotification({
          type: "success",
          message: "Rol actualizado con éxito.",
        })
      );
      return { id: roleId, ...roleData };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const deleteRole = createAsyncThunk(
  "globalAdmin/deleteRole",
  async ({ targetTenantId, roleId }, { dispatch, rejectWithValue }) => {
    try {
      const roleDocRef = doc(db, `tenants/${targetTenantId}/roles`, roleId);
      await deleteDoc(roleDocRef);
      dispatch(
        addNotification({
          type: "success",
          message: "Rol eliminado con éxito.",
        })
      );
      return roleId;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

const initialState = {
  tenants: [],
  managedTenant: {
    id: null,
    users: [],
    roles: [],
  },
  loading: false,
  error: null,
};

const globalAdminSlice = createSlice({
  name: "globalAdmin",
  initialState,
  reducers: {
    clearManagedTenant: (state) => {
      state.managedTenant = initialState.managedTenant;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAllTenants.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchAllTenants.fulfilled, (state, action) => {
        state.loading = false;
        state.tenants = action.payload;
      })
      .addCase(fetchAllTenants.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchTenantDetails.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchTenantDetails.fulfilled, (state, action) => {
        state.loading = false;
        state.managedTenant.id = action.payload.tenantId;
        state.managedTenant.users = action.payload.users;
        state.managedTenant.roles = action.payload.roles;
      })
      .addCase(fetchTenantDetails.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(createNewUser.fulfilled, (state, action) => {
        state.managedTenant.users.push(action.payload);
      })
      .addCase(updateUser.fulfilled, (state, action) => {
        const userIndex = state.managedTenant.users.findIndex(
          (u) => u.id === action.payload.userId
        );
        if (userIndex !== -1) {
          state.managedTenant.users[userIndex].roleId =
            action.payload.newRoleId;
        }
      })
      // --- REDUCER PARA ACTUALIZAR EL ESTADO DEL USUARIO ---
      .addCase(toggleUserActiveStatus.fulfilled, (state, action) => {
        const userIndex = state.managedTenant.users.findIndex(
          (u) => u.id === action.payload.userId
        );
        if (userIndex !== -1) {
          state.managedTenant.users[userIndex].isActive =
            action.payload.isActive;
        }
      })
      .addCase(addRole.fulfilled, (state, action) => {
        state.managedTenant.roles.push(action.payload);
      })
      .addCase(updateRole.fulfilled, (state, action) => {
        const roleIndex = state.managedTenant.roles.findIndex(
          (r) => r.id === action.payload.id
        );
        if (roleIndex !== -1) {
          state.managedTenant.roles[roleIndex] = action.payload;
        }
      })
      .addCase(deleteRole.fulfilled, (state, action) => {
        state.managedTenant.roles = state.managedTenant.roles.filter(
          (r) => r.id !== action.payload
        );
      })
      // Manejar estados de carga para todas las acciones de escritura
      .addMatcher(
        (action) => action.type.endsWith("/pending"),
        (state) => {
          state.loading = true;
          state.error = null;
        }
      )
      .addMatcher(
        (action) => action.type.endsWith("/fulfilled"),
        (state) => {
          state.loading = false;
        }
      )
      .addMatcher(
        (action) => action.type.endsWith("/rejected"),
        (state, action) => {
          state.loading = false;
          state.error = action.payload;
        }
      );
  },
});

export const { clearManagedTenant } = globalAdminSlice.actions;
export default globalAdminSlice.reducer;
